import { useState } from "react";
import {
  postVetcanAssistant,
  type VetcanAssistantResponse,
} from "../../lib/vetcanApi";
import StudioKnowledgeAssistantConfidenceNote from "./StudioKnowledgeAssistantConfidenceNote";

const EXAMPLE_QUESTIONS = [
  "What do Studio, Preview, and the Ops Dashboard each do?",
  "What should I review first on install day?",
  "What does launch control mean here?",
  "What stays staged versus live?",
  "Which approvals still happen outside Studio?",
  "What scheduling and payment boundaries still apply?",
] as const;

function topicLabel(topic: VetcanAssistantResponse["topic"]) {
  switch (topic) {
    case "artifact_truth":
      return "Artifact truth";
    case "approval_workflow":
      return "Approval workflow";
    case "preview_voice":
      return "Preview voice";
    case "scheduling_truth":
      return "Scheduling truth";
    case "payment_boundaries":
      return "Payment boundaries";
    case "offer_truth":
      return "Offer truth";
    case "compliance_boundaries":
      return "Compliance boundaries";
    case "unsupported":
      return "Blocked topic";
    default:
      return "System posture";
  }
}

function confidenceLabel(confidence: VetcanAssistantResponse["confidence"]) {
  switch (confidence) {
    case "limited":
      return "Limited evidence";
    case "not_applicable":
      return "Blocked request";
    default:
      return "Grounded evidence";
  }
}

function lineLabel(lineStart?: number, lineEnd?: number) {
  if (!lineStart) {
    return null;
  }
  if (!lineEnd || lineEnd === lineStart) {
    return `Line ${lineStart}`;
  }
  return `Lines ${lineStart}-${lineEnd}`;
}

export default function StudioKnowledgeAssistant() {
  const [question, setQuestion] = useState("");
  const [result, setResult] = useState<VetcanAssistantResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleAsk(promptOverride?: string) {
    const nextQuestion = (promptOverride ?? question).trim();
    if (!nextQuestion || loading) return;

    setQuestion(nextQuestion);
    setLoading(true);
    setError(null);

    try {
      const response = await postVetcanAssistant({
        question: nextQuestion,
        role: "client_viewer",
      });
      setResult(response);
    } catch (requestError) {
      const message =
        requestError instanceof Error
          ? requestError.message
          : "Knowledge assistant unavailable right now.";
      setError(
        `${message} Review Studio, Preview, and Dashboard directly until the bounded lookup responds again.`
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <section
      id="studio-guided-help"
      style={{
        marginTop: "1rem",
        borderRadius: 28,
        border: "1px solid rgba(54, 44, 35, 0.12)",
        background:
          "linear-gradient(180deg, rgba(255, 252, 248, 0.98), rgba(248, 244, 238, 0.96))",
        padding: "1.2rem",
        boxShadow: "0 22px 40px rgba(77, 61, 44, 0.08)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        <div style={{ maxWidth: 760 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.45rem",
              borderRadius: 999,
              padding: "0.24rem 0.62rem",
              background: "rgba(31, 76, 95, 0.08)",
              color: "#214b5d",
              fontSize: "0.74rem",
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
            }}
          >
            Guided help
          </div>
          <h2
            style={{
              margin: "0.75rem 0 0",
              fontSize: "1.45rem",
              lineHeight: 1.15,
              fontFamily: "Georgia, 'Times New Roman', serif",
            }}
          >
            Studio guided question bar
          </h2>
          <p style={{ margin: "0.65rem 0 0", color: "#4d453d", lineHeight: 1.65 }}>
            Ask client-safe questions about the company, the current system, how
            VetCan works today, and which actions still stay outside Studio.
          </p>
          <p style={{ margin: "0.5rem 0 0", color: "#5f554c", lineHeight: 1.65 }}>
            Best use on install day: ask it to explain Studio, Preview, Dashboard,
            launch control, staged-versus-live boundaries, and the current review
            order in plain English.
          </p>
          <p style={{ margin: "0.5rem 0 0", color: "#6a6056", lineHeight: 1.65 }}>
            This is the bounded help surface on the Studio page. It explains the
            current setup and boundaries, but it does not act like a general
            autonomous chat.
          </p>
          <p style={{ margin: "0.5rem 0 0", color: "#6a6056", lineHeight: 1.65 }}>
            Client-viewer mode only. Checked-in source truth plus current workspace
            artifact checks only. This surface does not read unpublished local draft
            choices, does not mutate runtime, and does not answer medical, PHI, or
            raw payment questions.
          </p>
        </div>

        <div
          aria-label="Studio client interaction mode"
          style={{
            display: "grid",
            gap: "0.45rem",
            minWidth: 250,
            color: "#3f372f",
          }}
        >
          <div style={{ fontWeight: 700 }}>Current mode</div>
          <div
            style={{
              borderRadius: 16,
              border: "1px solid rgba(54, 44, 35, 0.18)",
              background: "rgba(255, 255, 255, 0.94)",
              padding: "0.7rem 0.85rem",
              color: "#231f1a",
              fontSize: "0.96rem",
              fontWeight: 700,
            }}
          >
            Client viewer only
          </div>
          <div style={{ color: "#6a6056", lineHeight: 1.55, fontSize: "0.92rem" }}>
            This bar explains current setup and boundaries. It cannot change approval,
            publish, routing, or runtime state.
          </div>
        </div>
      </div>

      <div
        style={{
          marginTop: "1rem",
          display: "grid",
          gap: "0.85rem",
        }}
      >
        {!result && !error ? (
          <div
            style={{
              borderRadius: 18,
              border: "1px solid rgba(54, 44, 35, 0.1)",
              background: "rgba(255, 255, 255, 0.88)",
              padding: "0.95rem 1rem",
            }}
          >
            <div style={{ fontWeight: 700, color: "#2e2823" }}>
              Start with one of these guided questions
            </div>
            <div style={{ marginTop: "0.45rem", color: "#5f554c", lineHeight: 1.6 }}>
              Studio versus Preview versus Dashboard, launch control, staged versus
              live posture, install-day order, approval workflow, scheduling truth,
              payment-safe boundaries, and current checked-in docs and artifact
              checks.
            </div>
          </div>
        ) : null}

        <label style={{ display: "grid", gap: "0.5rem", fontWeight: 700, color: "#3f372f" }}>
          Ask Studio guided question bar
          <textarea
            aria-label="Ask Studio guided question bar"
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            placeholder="Example: What should I review first on install day?"
            rows={3}
            style={{
              width: "100%",
              borderRadius: 18,
              border: "1px solid rgba(54, 44, 35, 0.16)",
              background: "rgba(255, 255, 255, 0.94)",
              padding: "0.9rem 1rem",
              color: "#231f1a",
              resize: "vertical",
              lineHeight: 1.5,
              fontSize: "0.98rem",
            }}
          />
        </label>

        <div
          style={{
            display: "flex",
            gap: "0.7rem",
            flexWrap: "wrap",
          }}
        >
          {EXAMPLE_QUESTIONS.map((example) => (
            <button
              key={example}
              type="button"
              onClick={() => void handleAsk(example)}
              style={{
                borderRadius: 999,
                border: "1px solid rgba(41, 84, 103, 0.16)",
                background: "rgba(245, 249, 250, 0.96)",
                color: "#214b5d",
                padding: "0.58rem 0.84rem",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {example}
            </button>
          ))}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "0.8rem",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div style={{ color: "#6a6056", fontSize: "0.94rem" }}>
            Client-safe help only. Use it for explanation and walkthrough guidance, not
            approval bypass, publish action, or live control.
          </div>
          <button
            type="button"
            onClick={() => void handleAsk()}
            disabled={loading || !question.trim()}
            style={{
              borderRadius: 999,
              border: "1px solid rgba(25, 63, 78, 0.18)",
              background: loading ? "rgba(25, 63, 78, 0.25)" : "#183947",
              color: "#f7f4ee",
              padding: "0.76rem 1rem",
              fontWeight: 700,
              cursor: loading || !question.trim() ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Looking up current truth..." : "Ask client-safe question"}
          </button>
        </div>
      </div>

      {loading ? (
        <div
          aria-live="polite"
          style={{
            marginTop: "1rem",
            borderRadius: 18,
            border: "1px solid rgba(31, 76, 95, 0.14)",
            background: "rgba(241, 247, 250, 0.96)",
            padding: "0.9rem 1rem",
            color: "#214b5d",
            lineHeight: 1.6,
          }}
        >
          Looking up checked-in source truth plus current workspace artifact checks.
          This stays read-only and bounded to current system, process, company, and
          artifact questions.
        </div>
      ) : null}

      {error ? (
        <div
          role="alert"
          style={{
            marginTop: "1rem",
            borderRadius: 18,
            border: "1px solid rgba(162, 62, 62, 0.18)",
            background: "rgba(255, 244, 241, 0.95)",
            padding: "0.95rem 1rem",
            color: "#7c2f2f",
            lineHeight: 1.6,
          }}
        >
          {error}
        </div>
      ) : null}

      {result ? (
        <section
          aria-label="Studio client interaction answer"
          style={{
            marginTop: "1rem",
            borderRadius: 24,
            border: "1px solid rgba(54, 44, 35, 0.12)",
            background: "rgba(255, 255, 255, 0.94)",
            padding: "1rem",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "0.65rem",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <span
              style={{
                borderRadius: 999,
                padding: "0.2rem 0.55rem",
                background:
                  result.status === "blocked"
                    ? "rgba(162, 62, 62, 0.12)"
                    : "rgba(44, 93, 67, 0.12)",
                color: result.status === "blocked" ? "#7c2f2f" : "#224634",
                fontWeight: 700,
                fontSize: "0.8rem",
              }}
            >
              {result.status === "blocked" ? "Blocked" : "Answered"}
            </span>
            <span
              style={{
                borderRadius: 999,
                padding: "0.2rem 0.55rem",
                background: "rgba(31, 76, 95, 0.08)",
                color: "#214b5d",
                fontWeight: 700,
                fontSize: "0.8rem",
              }}
            >
              {topicLabel(result.topic)}
            </span>
            <span style={{ color: "#6a6056", fontSize: "0.86rem" }}>
              Client-safe answer
            </span>
            <span
              style={{
                borderRadius: 999,
                padding: "0.2rem 0.55rem",
                background:
                  result.confidence === "grounded"
                    ? "rgba(44, 93, 67, 0.12)"
                    : result.confidence === "limited"
                      ? "rgba(181, 141, 34, 0.16)"
                      : "rgba(162, 62, 62, 0.12)",
                color:
                  result.confidence === "grounded"
                    ? "#224634"
                    : result.confidence === "limited"
                      ? "#6d5210"
                      : "#7c2f2f",
                fontWeight: 700,
                fontSize: "0.8rem",
              }}
            >
              {confidenceLabel(result.confidence)}
            </span>
          </div>

          <p style={{ margin: "0.9rem 0 0", color: "#2e2823", lineHeight: 1.7 }}>
            {result.answer}
          </p>

          <StudioKnowledgeAssistantConfidenceNote
            confidence={result.confidence}
            note={result.confidenceNote}
            sourceSummaries={result.sourceSummaries}
          />

          <div
            style={{
              marginTop: "0.95rem",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: "0.9rem",
            }}
          >
            {result.evidence.length > 0 ? (
              <div>
                <h3 style={{ margin: 0, fontSize: "0.96rem" }}>Evidence excerpts</h3>
                <ul
                  style={{ margin: "0.55rem 0 0", paddingLeft: "1.1rem", color: "#4d453d" }}
                >
                  {result.evidence.map((evidence) => {
                    const lines = lineLabel(evidence.lineStart, evidence.lineEnd);
                    return (
                      <li
                        key={`${evidence.source}-${evidence.sectionLabel || evidence.label}`}
                        style={{ marginTop: "0.45rem", lineHeight: 1.55 }}
                      >
                        <strong>{evidence.label}</strong>
                        {evidence.sectionLabel ? ` (${evidence.sectionLabel})` : ""}:{" "}
                        {evidence.excerpt}
                        <div style={{ color: "#6a6056", fontSize: "0.86rem", marginTop: "0.2rem" }}>
                          {evidence.source}
                          {lines ? ` · ${lines}` : ""}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
            <div>
              <h3 style={{ margin: 0, fontSize: "0.96rem" }}>Boundaries</h3>
              <ul style={{ margin: "0.55rem 0 0", paddingLeft: "1.1rem", color: "#4d453d" }}>
                {result.boundaries.map((boundary) => (
                  <li key={boundary} style={{ marginTop: "0.32rem", lineHeight: 1.55 }}>
                    {boundary}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: "0.96rem" }}>Sources</h3>
              <ul style={{ margin: "0.55rem 0 0", paddingLeft: "1.1rem", color: "#4d453d" }}>
                {result.citations.map((citation) => (
                  <li
                    key={`${citation.source}-${citation.sectionLabel || citation.label}`}
                    style={{ marginTop: "0.32rem", lineHeight: 1.55 }}
                  >
                    <strong>{citation.label}:</strong> {citation.source}
                    {citation.sectionLabel ? ` · ${citation.sectionLabel}` : ""}
                    {lineLabel(citation.lineStart, citation.lineEnd)
                      ? ` · ${lineLabel(citation.lineStart, citation.lineEnd)}`
                      : ""}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      ) : null}
    </section>
  );
}
